/**
 * Inspectors for **Compare** and **Reconcile**.
 *
 * They ask the same three questions — what identifies the same record on both
 * sides, and which fields must agree — so they share a component. Reconcile
 * adds where the other side *is*: a vault it queries rather than a second wire.
 */

import { useEffect, useState } from "react";
import { Alert, MultiSelect, Select, Stack, Text } from "@mantine/core";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { listConnections, type ConnectionOut } from "@/shared/api/integration";
import {
  veevaDescribeObject,
  veevaListObjects,
  type FieldDefinition,
  type ObjectSummary,
} from "@/shared/api/integration-browse";
import { toastApiError } from "@/shared/ui/toast";

import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";

function FieldsToCompare({
  config,
  editable,
  onChange,
  options,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  options: string[];
}) {
  const chosen = Array.isArray(config.fields) ? config.fields.map(String) : [];
  return (
    <>
      <MultiSelect
        label="Fields that must agree"
        description="Leave empty to compare every column both sides have."
        data={Array.from(new Set([...options, ...chosen]))}
        value={chosen}
        onChange={(v) => onChange("fields", v)}
        disabled={!editable}
        searchable
        clearable
      />
      {chosen.length === 0 && (
        <Alert color="blue" variant="light">
          Comparing everything is the honest default, and noisy against a real target: a vault fills
          fields your data never had, and each one reads as a difference. Name the fields the
          migration is actually responsible for.
        </Alert>
      )}
    </>
  );
}

export function CompareConfig({
  config,
  editable,
  onChange,
  inputColumns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  inputColumns: Record<string, string[]>;
}) {
  const left = inputColumns["in-0"] ?? [];
  const right = inputColumns["in-1"] ?? [];
  const key = String(config.key ?? "");
  const rightKey = String(config.rightKey ?? "");

  return (
    <Stack gap="sm">
      <Select
        label="Key on the left"
        description="The column that identifies the same record on both sides."
        placeholder={left.length ? "Pick a column" : "Test the source to load columns"}
        data={Array.from(new Set([...left, key].filter(Boolean)))}
        value={key || null}
        onChange={(v) => onChange("key", v ?? "")}
        disabled={!editable}
        searchable
      />
      <Select
        label="Key on the right"
        description="Only if it is named differently there — which it usually is."
        placeholder="Same as the left"
        data={Array.from(new Set([...right, rightKey].filter(Boolean)))}
        value={rightKey || null}
        onChange={(v) => onChange("rightKey", v ?? "")}
        disabled={!editable}
        searchable
        clearable
      />
      <FieldsToCompare
        config={config}
        editable={editable}
        onChange={onChange}
        options={Array.from(new Set([...left, ...right]))}
      />
      <Text size="xs" c="dimmed">
        Three outputs: <b>missing</b> is on the left and not the right, <b>different</b> is the same
        key disagreeing, <b>extra</b> is on the right and not the left. Each carries{" "}
        <Text span ff="monospace">
          _key
        </Text>{" "}
        and{" "}
        <Text span ff="monospace">
          _differences
        </Text>
        .
      </Text>
    </Stack>
  );
}

export function ReconcileConfig({
  config,
  editable,
  onChange,
  inputColumns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  inputColumns: Record<string, string[]>;
}) {
  const getToken = useAccessToken();
  const [connections, setConnections] = useState<ConnectionOut[]>([]);
  const [objects, setObjects] = useState<ObjectSummary[]>([]);
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  // The node names a connection id or a `@role`; metadata takes an id.
  const {
    connectionId,
    role,
    settled: roleSettled,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const object = String(config.object ?? "");
  const incoming = inputColumns["in-0"] ?? [];

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const items = (await listConnections(await getToken())).items;
        if (!cancelled) setConnections(items.filter((c) => c.source_type === "veeva"));
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!connectionId) return;
    let cancelled = false;
    void (async () => {
      try {
        const items = (await veevaListObjects(await getToken(), connectionId)).items;
        if (!cancelled) setObjects(items);
      } catch (err) {
        toastApiError("Could not load the vault's objects", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  useEffect(() => {
    if (!connectionId || !object) {
      setFields([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const found = (await veevaDescribeObject(await getToken(), connectionId, object)).fields;
        if (!cancelled) setFields(found);
      } catch (err) {
        toastApiError("Could not load that object's fields", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, object]);

  const fieldNames = fields.map((f) => f.name);
  const unique = fields.filter((f) => f.unique).map((f) => f.name);

  return (
    <Stack gap="sm">
      <ConnectionRefSelect
        label="Vault to check"
        placeholder={connections.length ? "Pick a connection" : "No Veeva connections set up"}
        data={connections.map((c) => ({ value: c.connection_id, label: c.display_name }))}
        connectionId={connectionId}
        role={role}
        settled={roleSettled}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.connection_id === v)?.display_name ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          onChange("object", "");
          onChange("targetKey", "");
          onChange("fields", []);
        }}
      />
      <Select
        label="Object"
        placeholder={connectionId ? "Pick an object" : "Pick a connection first"}
        data={objects.map((o) => ({ value: o.name, label: o.label ?? o.name }))}
        value={object || null}
        onChange={(v) => onChange("object", v ?? "")}
        disabled={!editable || !connectionId}
        searchable
      />
      <Select
        label="Key in your data"
        description="The column holding each record's id from the source system."
        placeholder={incoming.length ? "Pick a column" : "Test the source to load columns"}
        data={Array.from(new Set([...incoming, String(config.key ?? "")].filter(Boolean)))}
        value={String(config.key ?? "") || null}
        onChange={(v) => onChange("key", v ?? "")}
        disabled={!editable}
        searchable
      />
      <Select
        label="Key in the vault"
        description="The field the load matched on — usually external_id__v."
        placeholder={unique.length ? "Pick a unique field" : "Pick an object first"}
        data={Array.from(new Set([...unique, String(config.targetKey ?? "")].filter(Boolean)))}
        value={String(config.targetKey ?? "") || null}
        onChange={(v) => onChange("targetKey", v ?? "")}
        disabled={!editable || !object}
        searchable
      />
      <FieldsToCompare
        config={config}
        editable={editable}
        onChange={onChange}
        options={fieldNames}
      />
      <Alert color="teal" variant="light" title="Reads only">
        <Text size="sm">
          Reconcile asks the vault what it holds <b>now</b> — a load report says what Vault accepted
          at the time, which is a different question after somebody has edited a record. It changes
          nothing, and reads back only the key and the fields above.
        </Text>
      </Alert>
    </Stack>
  );
}
